import { useCallback } from 'react';

import { GOOGLE_RECAPTCHA_SITE_KEY, GOOGLE_RECAPTCHA_SITE_KEY_OLD } from '~/constants';
import { FeatureFlag } from '~/lib/flags/flags';
import { useFeatureFlag } from '~/lib/flags/hooks';
import { log } from '~/util';

export const RECAPTCHA_CONTAINER_ID = 'google-recaptcha-container';

// Strings must stay in sync with pkg/recaptcha/actions.go.
export enum RecaptchaAction {
  Login = 'login',
  Register = 'register',
  PasswordResetRequest = 'password_reset_request',
  PasswordReset = 'password_reset',
  MfaSetup = 'mfa_setup',
  MfaVerify = 'mfa_verify',
  UpdateUser = 'update_user',
  Contact = 'contact',
}

const BADGE_ACTION = 'badge';

type ReCaptchaRenderParameters = {
  sitekey: string;
  action: string;
  badge: 'inline' | 'bottomleft' | 'bottomright';
  size: 'invisible';
};

type ReCaptchaExecute = {
  (widgetId: number): Promise<string>;
  (siteKey: string, options: { action: string }): Promise<string>;
};

type ReCaptchaEnterprise = {
  ready: (callback: () => void) => void;
  render: (container: string | HTMLElement, parameters: ReCaptchaRenderParameters) => number;
  execute: ReCaptchaExecute;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: ReCaptchaEnterprise;
    };
  }
}

let badgeRendered = false;

const getReCaptchaEnterprise = async (): Promise<ReCaptchaEnterprise> => {
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) {
    throw new Error('reCaptcha not loaded');
  }

  await new Promise<void>((resolve) => enterprise.ready(resolve));

  return enterprise;
};

const ensureBadgeRendered = async (siteKey: string): Promise<void> => {
  if (badgeRendered) {
    return;
  }

  if (!siteKey) {
    throw new Error('reCaptcha site key not configured');
  }

  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    throw new Error('reCaptcha container not found');
  }

  const enterprise = await getReCaptchaEnterprise();

  enterprise.render(container, {
    sitekey: siteKey,
    action: BADGE_ACTION,
    badge: 'inline',
    size: 'invisible',
  });

  badgeRendered = true;
};

const executeReCaptcha = async (siteKey: string, action: RecaptchaAction): Promise<string> => {
  await ensureBadgeRendered(siteKey);
  const enterprise = await getReCaptchaEnterprise();

  return enterprise.execute(siteKey, { action });
};

const useReCaptchaSiteKey = () => {
  const cutover = useFeatureFlag(FeatureFlag.CutoverRecaptcha, { default: false });

  return cutover ? GOOGLE_RECAPTCHA_SITE_KEY : GOOGLE_RECAPTCHA_SITE_KEY_OLD;
};

export const useReCAPTCHA = (): ((action: RecaptchaAction) => Promise<string>) => {
  const siteKey = useReCaptchaSiteKey();

  return useCallback(
    (action: RecaptchaAction) => {
      return executeReCaptcha(siteKey, action).catch((error: unknown) => {
        log({ name: 'reCaptcha error', type: 'error', context: { error } });
        const message = error instanceof Error ? error.message : error;
        if (message === 'Challenge cancelled by user.') {
          throw new Error('reCAPTCHA challenge not completed');
        }
        throw new Error('reCAPTCHA error');
      });
    },
    [siteKey]
  );
};
